import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import useInputValue from '../../hooks/useInputValue';
import Button from '../generic/Button/Button';
import ButtonGroup from '../generic/Button/ButtonGroup';
import LoaderButton from '../generic/Button/LoaderButton';
import FormGroup from '../generic/Forms/FormGroup';
import TextArea from '../generic/Forms/TextArea';
import Text from '../generic/Text';

type Props = {
  isPending: boolean;
  show: boolean;
  onHide: () => void;
  onSubmit: (comment: string) => void;
};

/**
 * Malware disputes only support a single reason (FALSE_POSITIVE), so unlike the
 * quality and vulnerability dispute modals this one collects a comment alone.
 */
export const ModalReportMalwareDispute: VoidFunctionComponent<Props> = ({
  isPending,
  show,
  onHide,
  onSubmit,
}) => {
  const [comment, onCommentChange] = useInputValue('');

  const handleSubmit = async () => {
    if (isPending) {
      return;
    }

    await onSubmit(comment);
    onCommentChange('');
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
    >
      <Modal.Header>
        <Text.H2>
          Malware Feedback
        </Text.H2>
      </Modal.Header>
      <Modal.Body>
        <p className="margin-bottom-sm">
          Let us know why you believe this package was incorrectly flagged as malware.
          Your feedback will be reviewed by the FOSSA team to help improve our malware detection.
        </p>
        <FormGroup>
          <TextArea
            label="Dispute comment"
            placeholder="Give us any extra information you think can be useful."
            value={comment}
            onChange={onCommentChange}
            isDisabled={isPending}
            isRequired
          />
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup direction="right">
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
            isDisabled={isPending}
          >
            Cancel
          </Button>
          <LoaderButton
            type="primary"
            size="medium"
            onClick={handleSubmit}
            isDisabled={isPending}
            isLoading={isPending}
          >
            Submit Feedback
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};
